import { useTranslation } from "react-i18next";
import type { Lang } from "../i18n/lang";
import { localizedPath } from "../i18n/lang";
import { SITE } from "../config/site";
import { SITE_URL, SITE_ADDRESS, SLOGAN, ROUTE_META, type RoutePath } from "../config/seo";
import {
  formatPremiumPrice,
  type PublicSettings,
  usePublicSettings,
} from "../lib/usePublicSettings";

type FaqItem = { q: string; a: string };

// Rendered into the body (not <Head>) via dangerouslySetInnerHTML so the JSON is emitted raw
// at prerender — no HTML-entity escaping of quotes. Google reads ld+json anywhere in the doc.
function JsonLdScript({ data }: { data: object }) {
  // Guard against a literal </script> breaking out of the tag (defensive; our copy has none).
  const json = JSON.stringify(data).replace(/</g, "\\u003c");
  return (
    <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: json }} />
  );
}

const organization = (settings: PublicSettings, lang: Lang) => ({
  "@context": "https://schema.org",
  "@type": "Organization",
  name: SITE.name,
  slogan: SLOGAN[lang],
  url: SITE_URL,
  logo: `${SITE_URL}/favicon.svg`,
  sameAs: [SITE.social.instagram, settings.supportTelegramLink],
  contactPoint: {
    "@type": "ContactPoint",
    telephone: settings.supportPhone,
    email: SITE.email,
    contactType: "customer support",
    areaServed: "UZ",
    availableLanguage: ["uz", "ru"],
  },
  address: {
    "@type": "PostalAddress",
    ...SITE_ADDRESS,
  },
});

const website = () => ({
  "@context": "https://schema.org",
  "@type": "WebSite",
  name: SITE.name,
  url: SITE_URL,
  inLanguage: ["uz", "ru"],
});

// Business (merchant) app. No aggregateRating — we have no real review data.
const mobileApp = (description: string) => ({
  "@context": "https://schema.org",
  "@type": "MobileApplication",
  name: "Nasiyah Biznes",
  operatingSystem: "iOS, Android",
  applicationCategory: "BusinessApplication",
  description,
  url: SITE_URL,
  installUrl: SITE.stores.appStore,
  downloadUrl: [SITE.stores.appStore, SITE.stores.googlePlay],
  offers: { "@type": "Offer", price: "0", priceCurrency: "UZS" },
});

const faqPage = (items: FaqItem[]) => ({
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: items.map((it) => ({
    "@type": "Question",
    name: it.q,
    acceptedAnswer: { "@type": "Answer", text: it.a },
  })),
});

const breadcrumb = (lang: Lang, path: RoutePath, name: string) => ({
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: [
    {
      "@type": "ListItem",
      position: 1,
      name: SITE.name,
      item: `${SITE_URL}${localizedPath("/", lang)}`,
    },
    {
      "@type": "ListItem",
      position: 2,
      name,
      item: `${SITE_URL}${localizedPath(path, lang)}`,
    },
  ],
});

/** Landing structured data: Organization + WebSite + MobileApplication + FAQPage. */
export function LandingJsonLd({ lang }: { lang: Lang }) {
  const { t } = useTranslation();
  const settings = usePublicSettings();
  const items = t("faq.items", {
    returnObjects: true,
    months: settings.freeTierMonths,
    price: formatPremiumPrice(settings.premiumPrice),
  }) as FaqItem[];
  const description = ROUTE_META["/"][lang].description;
  return (
    <>
      <JsonLdScript data={organization(settings, lang)} />
      <JsonLdScript data={website()} />
      <JsonLdScript data={mobileApp(description)} />
      <JsonLdScript data={faqPage(items)} />
    </>
  );
}

/** Legal-page structured data: Organization + WebSite + BreadcrumbList. */
export function LegalJsonLd({ lang, path }: { lang: Lang; path: RoutePath }) {
  const name = ROUTE_META[path][lang].title;
  const settings = usePublicSettings();
  return (
    <>
      <JsonLdScript data={organization(settings, lang)} />
      <JsonLdScript data={website()} />
      <JsonLdScript data={breadcrumb(lang, path, name)} />
    </>
  );
}
